/**
 * The single source of truth for the canonical-entity vocabulary (MAR-129).
 *
 * **Three orthogonal axes. Never fuse two facts into one string.**
 *
 * | Axis  | Column                  | Answers                     |
 * |-------|-------------------------|-----------------------------|
 * | Who   | `entities.kind`         | what sort of actor          |
 * | What  | `entities.id_type`      | what sort of identifier     |
 * | Where | `entities.jurisdiction` | which country issued it     |
 *
 * `DUI` fused *what* + *where*; `Cédula de Persona Jurídica` fused *what* + *where* + *who*; `HRB`
 * fused *what* + *where*. None of them are stored any more — they are **composed for display** from
 * the enum's i18n label plus the country. There is deliberately no `company_tax_id` member: that is
 * `kind=organization` × `id_type=tax_id`, and adding it would re-fuse the *who* axis back in.
 *
 * Validation lives here, in code, not in a Postgres enum. Authored data (the editor picker, a
 * published workflow definition) must be a member and fails loudly if it is not; extracted data goes
 * through `normalizeIdType` first, which maps a raw model- or MRZ-produced string onto the enum and
 * recovers the country it had fused in.
 *
 * **Do not re-declare these literals anywhere else.** `tests/unit/entityVocabulary.test.ts` asserts this
 * file is their only home under `apps/web`.
 */

/**
 * The three axes themselves are declared in `shared/vocabulary/crossService.ts` and re-exported
 * here, because the document-processing service must be shown the SAME lists — it is the extractor
 * that produces the values this file then normalizes. See that file for per-member coverage and for
 * how its copy is generated. Everything below (normalization, compatibility, labels) is web-only and
 * stays here.
 */
export {
  ENTITY_KINDS, ENTITY_ID_TYPES, PARTY_KINDS,
  ENTITY_FACTS, ENTITY_FACTS_BY_KIND, ENTITY_FACTS_BY_JURISDICTION,
  entityFactsForKind, entityAttributeFactsForKind,
  IDENTIFIER_PROPERTIES, isIdentifierProperty, idTypeForProperty,
  type EntityKind, type EntityIdType, type PartyKind,
  type EntityFact, type IdentifierProperty,
} from '../vocabulary/crossService'
import { ENTITY_KINDS, ENTITY_ID_TYPES, PARTY_KINDS, type EntityKind, type EntityIdType, type PartyKind } from '../vocabulary/crossService'

/**
 * Which identifier categories can stand in for one another when OFFERING a suggestion (MAR-176).
 *
 * A run-form input that asks for a national id is asking the person to identify themselves, and a
 * passport or a residence permit does that just as validly — the exact-match filter meant a client
 * whose only stored identifier was a passport got "nothing in this client", which reads as the
 * feature being broken rather than as a deliberate refusal.
 *
 * The three person-identity documents are therefore mutually offerable. `tax_id`,
 * `business_registration` and `other` are NOT: a NIT is not an identity document, and a form asking
 * for a company's registry number is asking for that number specifically, not for whatever
 * identifier the company happens to have on file.
 *
 * **This widens what is OFFERED, never what is stored.** The candidate keeps its own `id_type`, is
 * labelled with it, and an exact match is always ordered first — the user picks, and sees what they
 * picked. Compare `normalizeIdType`, which is about reading data in; this is about display out.
 */
export const COMPATIBLE_ID_TYPES: Partial<Record<EntityIdType, readonly EntityIdType[]>> = {
  national_id: ['national_id', 'passport', 'foreigner_id'],
  passport: ['passport', 'national_id', 'foreigner_id'],
  foreigner_id: ['foreigner_id', 'national_id', 'passport'],
}

/**
 * The identifier categories to offer for an input that asked for `idType`, exact match first.
 * An unknown or null type offers nothing — see `resolveScopedEntities`, where "names nothing"
 * deliberately matches nothing rather than becoming a wildcard.
 */
export function offerableIdTypes(idType: EntityIdType): readonly EntityIdType[] {
  return COMPATIBLE_ID_TYPES[idType] ?? [idType]
}

/** `legal_form` suggestions (CANONICAL-ENTITIES §4) — free text; these only power a datalist. */
export const LEGAL_FORM_SUGGESTIONS = ['sa_de_cv', 's_de_rl', 'sa', 'srl', 'llc', 'inc', 'corp', 'gmbh', 'ug', 'ag', 'ltd', 'plc', 'bv']

export function isEntityKind(value: unknown): value is EntityKind {
  return typeof value === 'string' && (ENTITY_KINDS as readonly string[]).includes(value)
}

export function isEntityIdType(value: unknown): value is EntityIdType {
  return typeof value === 'string' && (ENTITY_ID_TYPES as readonly string[]).includes(value)
}

export function isPartyKind(value: unknown): value is PartyKind {
  return typeof value === 'string' && (PARTY_KINDS as readonly string[]).includes(value)
}

/** Strip accents and case so `Cédula` and `cédula` and `CEDULA` are one key. */
function foldKey(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

export interface NormalizedIdType {
  /** null when the raw value names no real identifier ("no especificado", a template blank). */
  idType: EntityIdType | null
  /**
   * The country the raw value had fused in, ISO 3166-1 alpha-2 — only when the name itself carries
   * it (`HRB` is German by definition). **Never guessed**: `Cédula de Identidad` and `NIT` name the
   * identifier in a dozen countries each, so they resolve to a type with a null jurisdiction and the
   * country has to come from the document.
   */
  jurisdiction: string | null
  /**
   * The actor the raw value had fused in — `cédula de persona jurídica` is by definition a company.
   * Only set where the name is decisive; otherwise the caller's own `kind` stands.
   */
  kind: EntityKind | null
}

const NOTHING: NormalizedIdType = { idType: null, jurisdiction: null, kind: null }

/**
 * Raw strings observed in the wild (LLM extraction was told to emit "DUI, NIT, Pasaporte, etc.";
 * MRZ fused issuing state into the type) mapped onto the three axes.
 *
 * Keys are accent- and case-folded by `foldKey`. Values only carry a jurisdiction or kind where the
 * name itself is decisive — see `NormalizedIdType`.
 */
const RAW_ID_TYPES: Record<string, NormalizedIdType> = {
  // — identity documents, country-bearing —
  'dui': { idType: 'national_id', jurisdiction: 'SV', kind: 'person' },
  'documento unico de identidad': { idType: 'national_id', jurisdiction: 'SV', kind: 'person' },
  'cui': { idType: 'national_id', jurisdiction: 'GT', kind: 'person' },
  'dpi': { idType: 'national_id', jurisdiction: 'GT', kind: 'person' },
  'dni': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'personalausweis': { idType: 'national_id', jurisdiction: 'DE', kind: 'person' },
  'ine': { idType: 'national_id', jurisdiction: 'MX', kind: 'person' },
  'curp': { idType: 'national_id', jurisdiction: 'MX', kind: 'person' },

  // — identity documents, country ambiguous (cédula is the national id in CR, PA, DO, EC, NI, …) —
  'cedula': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'cedula de identidad': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'cedula de identidad personal': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'cedula de identidad ciudadana': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'documento de identidad': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'documento de identidad nacional': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  // Generic names: the DOCUMENT is a national id, but the phrase names no country (DNI is Peruvian,
  // Argentine and Spanish; "Documento Nacional de Identificación" is used across the isthmus). The
  // issuer has to come from the document, never from the shape of the number.
  'documento nacional de identidad': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'documento nacional de identificacion': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'numero de identidad': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'documento de identificacion salvadorena': { idType: 'national_id', jurisdiction: 'SV', kind: 'person' },
  'dpi cui': { idType: 'national_id', jurisdiction: 'GT', kind: 'person' },
  'national id': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'identity document': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'identity card': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'id card': { idType: 'national_id', jurisdiction: null, kind: 'person' },
  'identidad': { idType: 'national_id', jurisdiction: null, kind: 'person' },

  // — travel —
  'pasaporte': { idType: 'passport', jurisdiction: null, kind: 'person' },
  'passport': { idType: 'passport', jurisdiction: null, kind: 'person' },
  'reisepass': { idType: 'passport', jurisdiction: 'DE', kind: 'person' },
  'pasaporte salvadoreno': { idType: 'passport', jurisdiction: 'SV', kind: 'person' },
  'pasaporte mexicano': { idType: 'passport', jurisdiction: 'MX', kind: 'person' },
  // A type naming TWO documents ("Pasaporte Mexicano / Carnet de residente") is the extractor
  // hedging. Take the first, which is the one the number belongs to.
  'pasaporte mexicano carnet de residente': { idType: 'passport', jurisdiction: 'MX', kind: 'person' },

  // — residence —
  'carnet de residencia': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },
  'carne de residencia': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },
  'carnet de extranjeria': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },
  'nie': { idType: 'foreigner_id', jurisdiction: 'ES', kind: 'person' },
  'aufenthaltstitel': { idType: 'foreigner_id', jurisdiction: 'DE', kind: 'person' },
  'residence permit': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },
  'foreigner id': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },
  // The CA-4 resident card (El Salvador, Guatemala, Honduras, Nicaragua) — a residence document,
  // and deliberately country-less: it is issued by whichever of the four the holder resides in.
  'carnet de identificacion de centroamericano residente': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },
  'carne de identificacion de centroamericano residente': { idType: 'foreigner_id', jurisdiction: null, kind: 'person' },

  // — tax. NIT is SV, GT and CO; VAT is any EU member, and the country is in the number itself. —
  'nit': { idType: 'tax_id', jurisdiction: null, kind: null },
  'numero de identificacion tributaria': { idType: 'tax_id', jurisdiction: null, kind: null },
  'nrc': { idType: 'tax_id', jurisdiction: 'SV', kind: null },
  'rtn': { idType: 'tax_id', jurisdiction: 'HN', kind: null },
  'rfc': { idType: 'tax_id', jurisdiction: 'MX', kind: null },
  'ruc': { idType: 'tax_id', jurisdiction: null, kind: null },
  'nif': { idType: 'tax_id', jurisdiction: 'ES', kind: null },
  'cif': { idType: 'tax_id', jurisdiction: 'ES', kind: 'organization' },
  'vat': { idType: 'tax_id', jurisdiction: null, kind: 'organization' },
  'vat number': { idType: 'tax_id', jurisdiction: null, kind: 'organization' },
  'ust idnr': { idType: 'tax_id', jurisdiction: 'DE', kind: 'organization' },
  'steuernummer': { idType: 'tax_id', jurisdiction: 'DE', kind: null },
  'ein': { idType: 'tax_id', jurisdiction: 'US', kind: 'organization' },
  'tax id': { idType: 'tax_id', jurisdiction: null, kind: null },
  // Observed only as a truncated RTN on a Honduran company. A tax id either way; the country is not
  // in the two letters, so it stays unknown.
  'rt': { idType: 'tax_id', jurisdiction: null, kind: null },
  // A *persona jurídica* cédula is the company tax registration, not an identity document.
  'cedula de persona juridica': { idType: 'tax_id', jurisdiction: null, kind: 'organization' },
  'cedula juridica': { idType: 'tax_id', jurisdiction: null, kind: 'organization' },

  // — commercial registry —
  'hrb': { idType: 'business_registration', jurisdiction: 'DE', kind: 'organization' },
  'hra': { idType: 'business_registration', jurisdiction: 'DE', kind: 'organization' },
  'handelsregister': { idType: 'business_registration', jurisdiction: 'DE', kind: 'organization' },
  'handelsregisternummer': { idType: 'business_registration', jurisdiction: 'DE', kind: 'organization' },
  'kbo': { idType: 'business_registration', jurisdiction: 'BE', kind: 'organization' },
  'bce': { idType: 'business_registration', jurisdiction: 'BE', kind: 'organization' },
  'crossroads bank for enterprise number': { idType: 'business_registration', jurisdiction: 'BE', kind: 'organization' },
  'crossroads bank for enterprises number': { idType: 'business_registration', jurisdiction: 'BE', kind: 'organization' },
  'companies house number': { idType: 'business_registration', jurisdiction: 'GB', kind: 'organization' },
  'company number': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'registration number': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'numero de registro': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'registro de comercio': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  // The standard Spanish-language names for a commercial registry. Observed live from the extractor
  // on a Guatemalan share-purchase agreement, where both spellings appeared in one document.
  'registro mercantil': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'registro de empresa mercantil': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'numero de registro mercantil': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'matricula de comercio': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'registro publico de panama': { idType: 'business_registration', jurisdiction: 'PA', kind: 'organization' },
  'registro publico': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  'registry number': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },
  // A community association is constituted BY a municipal agreement, so that number is the closest
  // thing it has to a registration. Mapped explicitly rather than left to fall through to `other`,
  // so it reads as a decision instead of an oversight.
  'acuerdo municipal': { idType: 'business_registration', jurisdiction: null, kind: 'organization' },

  // — explicit non-values. These are not identifiers; they must not become `other`. —
  // The extractor emits the STRING "null" (not a JSON null) for a party it could not identify —
  // observed live. Without this it folds to an unrecognised word and becomes `other`, i.e. a claim
  // that the party carries some identifier we could not categorise, which is the opposite of true.
  'null': NOTHING,
  'undefined': NOTHING,
  'no especificado': NOTHING,
  'not specified': NOTHING,
  'ninguno': NOTHING,
  'none': NOTHING,
  'n a': NOTHING,
  'desconocido': NOTHING,
  'unknown': NOTHING,
}

/**
 * Map a raw, human- or model-written identifier name onto the three axes.
 *
 * Already-canonical values pass straight through, so this is idempotent and safe to run over a
 * partially migrated column. An unrecognised non-empty string becomes `other` rather than throwing:
 * a document naming an identifier we have no category for is a real identifier, and losing it would
 * be worse than filing it loosely. Only the explicit non-values and blanks resolve to a null type.
 */
export function normalizeIdType(raw: string | null | undefined): NormalizedIdType {
  if (raw == null) return NOTHING
  const key = foldKey(raw)
  if (!key) return NOTHING
  // A template blank ("_____________") folds to empty only if it has no digits; guard the rest.
  if (!/[a-z0-9]/.test(key)) return NOTHING
  if (isEntityIdType(key.replace(/ /g, '_'))) {
    return { idType: key.replace(/ /g, '_') as EntityIdType, jurisdiction: null, kind: null }
  }
  return RAW_ID_TYPES[key] ?? { idType: 'other', jurisdiction: null, kind: null }
}

/**
 * ISO 3166-1 alpha-3 → alpha-2, for the codes that actually reach us.
 *
 * An MRZ states its issuing country in alpha-3 (`SLV`), while `entities.jurisdiction` and the org's
 * `main_jurisdiction` are alpha-2 (`SV`). Only the countries the corpus plausibly contains are
 * listed: an unknown code resolves to null rather than to a guess, and null is an honest answer
 * (decision: where jurisdiction is genuinely unknown it stays null, never a default).
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  SLV: 'SV', GTM: 'GT', HND: 'HN', NIC: 'NI', CRI: 'CR', PAN: 'PA', BLZ: 'BZ',
  MEX: 'MX', DOM: 'DO', CUB: 'CU', COL: 'CO', VEN: 'VE', ECU: 'EC', PER: 'PE',
  BOL: 'BO', CHL: 'CL', ARG: 'AR', URY: 'UY', PRY: 'PY', BRA: 'BR',
  USA: 'US', CAN: 'CA',
  ESP: 'ES', PRT: 'PT', FRA: 'FR', DEU: 'DE', AUT: 'AT', CHE: 'CH', ITA: 'IT',
  NLD: 'NL', BEL: 'BE', LUX: 'LU', GBR: 'GB', IRL: 'IE', POL: 'PL', SWE: 'SE',
  NOR: 'NO', DNK: 'DK', FIN: 'FI',
  CHN: 'CN', JPN: 'JP', KOR: 'KR', IND: 'IN', ISR: 'IL', TUR: 'TR', AUS: 'AU', NZL: 'NZ', ZAF: 'ZA',
}

/**
 * The issuing countries an identifier can be filed under, alpha-2, sorted.
 *
 * Derived from the alpha-3 map above rather than written out a second time — that map already says
 * which countries the corpus plausibly contains, and MAR-129's rule is that the vocabulary has
 * exactly one declaration. Rendered with `Intl.DisplayNames` at the call site, so there is no list
 * of country NAMES here to translate or let rot.
 */
export const ID_JURISDICTIONS: readonly string[] = Object.values(ALPHA3_TO_ALPHA2).sort()

/**
 * Canonicalize a country code to ISO 3166-1 alpha-2.
 *
 * Accepts what the sources actually emit — an alpha-2 already, an MRZ alpha-3, surrounding
 * whitespace — and returns null for anything else. **Never guesses**: a country we cannot resolve
 * is stored as null, because a wrong country silently splits or merges identities.
 */
export function toAlpha2(raw: string | null | undefined): string | null {
  if (!raw) return null
  const code = raw.trim().toUpperCase()
  if (/^[A-Z]{2}$/.test(code)) return code
  if (/^[A-Z]{3}$/.test(code)) return ALPHA3_TO_ALPHA2[code] ?? null
  return null
}

/**
 * Human-readable names for the enum, for text that is NOT rendered through vue-i18n — chiefly the
 * Spanish prompts the document agents build, where "national_id: 12345678-9" reads as a leaked
 * internal token and gives the model nothing. Typed as a total `Record`, so adding a member to
 * `ENTITY_ID_TYPES` fails the typecheck here until it is named.
 *
 * The UI does NOT use this: it renders `idTypeLabelKey()` through i18n. Two audiences, one enum.
 */
export const ID_TYPE_NAMES: Record<'es' | 'en', Record<EntityIdType, string>> = {
  es: {
    national_id: 'Documento de identidad',
    passport: 'Pasaporte',
    foreigner_id: 'Carné de residente',
    tax_id: 'Identificación tributaria',
    business_registration: 'Registro mercantil',
    other: 'Identificación',
  },
  en: {
    national_id: 'National ID',
    passport: 'Passport',
    foreigner_id: 'Foreigner ID',
    tax_id: 'Tax ID',
    business_registration: 'Commercial register',
    other: 'Identifier',
  },
}

/**
 * The plain name of an identifier category, for text that is not rendered through vue-i18n — the
 * agents' prompts, and the `documentLabel` a workflow template interpolates into legal prose.
 *
 * Country-neutral by decision: there is no `(type, jurisdiction) → "Documento Único de Identidad"`
 * reverse map, and a document naming the wrong local instrument is worse than one naming the right
 * general category. "Documento de identidad número 12345678-9" is true of a DUI, a CUI and a
 * cédula alike; "DUI número …" is false for two of the three.
 */
export function idTypeName(idType: string | null | undefined, locale: 'es' | 'en' = 'es'): string {
  const canonical = isEntityIdType(idType) ? idType : normalizeIdType(idType).idType
  return ID_TYPE_NAMES[locale][canonical ?? 'other']
}

/**
 * Render an identity as label + country + number — the composition that replaces a stored display
 * name. "Documento de identidad (SV): 12345678-9", never "DUI: …" and never "national_id: …".
 *
 * The country is appended only when known; an unknown issuer simply says nothing about one, which
 * is the honest rendering of a null jurisdiction.
 */
export function formatIdentity(
  idType: string | null | undefined,
  jurisdiction: string | null | undefined,
  idNumber: string | null | undefined,
  locale: 'es' | 'en' = 'es',
): string | null {
  if (!idNumber) return null
  const label = idTypeName(idType, locale)
  const country = toAlpha2(jurisdiction)
  return `${label}${country ? ` (${country})` : ''}: ${idNumber}`
}

/**
 * The i18n key for an id_type's label.
 *
 * Rendering is **label + country**, composed at the point of display — there is deliberately no
 * `(type, jurisdiction) → "DUI"` lookup table. We store the country, so "National ID · El Salvador"
 * needs no map to produce, and it stays correct for a country nobody wrote a mapping for. The
 * labels themselves are therefore country-neutral: `national_id` is "National ID", never "DUI / ID".
 */
export function idTypeLabelKey(idType: EntityIdType): string {
  return `clients.detail.idTypes.${idType}`
}
